// src/pages/ListaRevendas.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listarRevendasPaginado,
  bloquearRevenda,
  desbloquearRevenda,
  excluirRevenda,
} from "../services/api";

import {
  ShieldOff,
  ShieldCheck,
  Users,
  KeyRound,
  Edit,
  Trash2,
  Search,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ArrowUpDown,
} from "lucide-react";
import { useFeedbackAlert } from "./common/useFeedbackAlert";

const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";

const HeaderSortButton = ({ label, field, sortBy, desc, onSort }) => {
  const active = sortBy === field;

  return (
    <button
      type="button"
      className={`
        inline-flex items-center gap-1 transition
        ${active ? "text-blue-600 dark:text-blue-300" : "text-gray-600 dark:text-slate-200"}
        hover:text-blue-700 dark:hover:text-blue-200
      `}
      onClick={() => onSort(field)}
      title="Ordenar"
    >
      {label} <ArrowUpDown size={16} />
      {active && <span className="text-xs ml-1 opacity-80">{desc ? "↓" : "↑"}</span>}
    </button>
  );
};

const StatusBadge = ({ ativo }) => {
  const isAtivo = toBool(ativo);

  return (
    <span
      className={`
        inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border
        ${
          isAtivo
            ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-200 dark:border-green-500/20"
            : "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-200 dark:border-red-500/20"
        }
      `}
    >
      {isAtivo ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
      {isAtivo ? "Ativa" : "Bloqueada"}
    </span>
  );
};

const ListaRevendas = ({ token }) => {
  const { showError, showSuccess, FeedbackAlert } = useFeedbackAlert();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(60);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  const [busca, setBusca] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const debounceRef = useRef(null);

  const [sortBy, setSortBy] = useState("razaosocial"); // id, cnpj, razaosocial, nomefantasia, ativo, criadoem
  const [desc, setDesc] = useState(false);

  const fetchData = async (opts = {}) => {
    const res = await listarRevendasPaginado(
      { page, pageSize, search: searchTerm, sortBy, desc, ...opts },
      token
    );

    const data = res.data;

    setItems(data.items || []);
    setPage(data.page);
    setPageSize(data.pageSize);
    setTotalItems(data.totalItems);
    setTotalPages(data.totalPages);

    // seu backend atual não manda hasNext/hasPrevious
    const tp = Number(data.totalPages || 1);
    const pg = Number(data.page || 1);
    setHasPrevious(pg > 1);
    setHasNext(pg < tp);
  };

  useEffect(() => {
    fetchData().catch((err) => console.error("Erro ao listar revendas:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchTerm, sortBy, desc]);

  const handleBuscaChange = (e) => {
    const valor = e.target.value;
    setBusca(valor);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearchTerm(valor);
    }, 400);
  };

  const handleSort = (field) => {
    if (sortBy === field) setDesc((d) => !d);
    else {
      setSortBy(field);
      setDesc(false);
    }
    setPage(1);
  };

  const handleEditar = (id) => navigate(`/revendas/editar/${id}`);

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta revenda?")) return;
    try {
      await excluirRevenda(id, token);
      await fetchData();
      showSuccess("Revenda excluída com sucesso!");
    } catch (err) {
      console.error(err);
      showError(err, "Erro ao excluir revenda.");
    }
  };

  const handleBloquear = async (r) => {
    if (!window.confirm(`Bloquear a revenda "${r.nomeFantasia || r.razaoSocial}"?`)) return;
    try {
      await bloquearRevenda(r.id, token);
      await fetchData();
    } catch (err) {
      console.error(err);
      showError(err, "Erro ao bloquear revenda.");
    }
  };

  const handleDesbloquear = async (r) => {
    if (!window.confirm(`Desbloquear a revenda "${r.nomeFantasia || r.razaoSocial}"?`)) return;
    try {
      await desbloquearRevenda(r.id, token);
      await fetchData();
    } catch (err) {
      console.error(err);
      showError(err, "Erro ao desbloquear revenda.");
    }
  };

  const verClientesDaRevenda = (revendaId) => navigate(`/clientes?revendaId=${revendaId}`);
  const verLicencasDaRevenda = (revendaId) => navigate(`/revendas/${revendaId}/licencas`);
  const verUsuariosDaRevenda = (revendaId) => navigate(`/revendas/usuarios?revendaId=${revendaId}`);

  const paginaInfo = useMemo(() => {
    const ini = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const fim = Math.min(page * pageSize, totalItems);
    return `${ini}-${fim} de ${totalItems}`;
  }, [page, pageSize, totalItems]);

  return (
    <>
      <FeedbackAlert />
    <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
      {/* header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Listagem de Revendas</h2>
          <p className="text-xs text-gray-500 dark:text-slate-300/80 mt-1">
            Ações: bloquear/desbloquear, ver clientes e licenças por revenda.
          </p>
        </div>
      </div>

      {/* filtros */}
      <div className="rounded-2xl p-4 mb-6 zen-card border-gray-200 dark:bg-white/5 dark:border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-8">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Buscar</label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Buscar por CNPJ / Razão / Nome Fantasia"
                value={busca}
                onChange={handleBuscaChange}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>
          </div>

          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Itens por página
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                zen-card border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
              title="Itens por página"
            >
              {[10, 20, 30, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}/página
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* tabela */}
      <div className="overflow-x-auto">
        <div className="min-w-full rounded-2xl zen-card dark:border-white/10 dark:bg-white/5">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton label="CNPJ" field="cnpj" sortBy={sortBy} desc={desc} onSort={handleSort} />
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton
                    label="Razão Social"
                    field="razaosocial"
                    sortBy={sortBy}
                    desc={desc}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton
                    label="Nome Fantasia"
                    field="nomefantasia"
                    sortBy={sortBy}
                    desc={desc}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton label="Status" field="ativo" sortBy={sortBy} desc={desc} onSort={handleSort} />
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Ações</th>
              </tr>
            </thead>

            <tbody className="text-gray-700 dark:text-slate-200">
              {items.map((r) => {
                const isAtivo = toBool(r.ativo);

                return (
                  <tr
                    key={r.id}
                    className="
                      border-t border-gray-200 hover:bg-gray-50 transition
                      dark:border-white/10 dark:hover:bg-white/5
                    "
                  >
                    <td className="py-3 px-4">{r.cnpj}</td>
                    <td className="py-3 px-4">{r.razaoSocial}</td>
                    <td className="py-3 px-4">{r.nomeFantasia}</td>

                    <td className="text-center py-3 px-4">
                      <StatusBadge ativo={r.ativo} />
                    </td>

                    <td className="text-center py-3 px-4">
                      <div className="inline-flex items-center justify-center gap-1 flex-wrap">
                        {/* Bloquear / Desbloquear */}
                        <button
                          className={`
                            px-3 py-2 rounded-xl text-sm font-semibold transition border inline-flex items-center gap-2
                            ${
                              isAtivo
                                ? "text-red-700 bg-red-50 border-red-200 hover:bg-red-100 dark:text-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:hover:bg-red-500/15"
                                : "text-green-700 bg-green-50 border-green-200 hover:bg-green-100 dark:text-green-200 dark:bg-green-500/10 dark:border-green-500/20 dark:hover:bg-green-500/15"
                            }
                          `}
                          onClick={() => (isAtivo ? handleBloquear(r) : handleDesbloquear(r))}
                          title={isAtivo ? "Bloquear revenda" : "Desbloquear revenda"}
                        >
                          {isAtivo ? <ShieldOff size={16} /> : <ShieldCheck size={16} />}
                          {isAtivo ? "Bloquear" : "Desbloquear"}
                        </button>

                        {/* Ver clientes */}
                        <button
                          className="
                            px-3 py-2 rounded-xl text-sm font-semibold transition border inline-flex items-center gap-2
                            text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100
                            dark:text-blue-200 dark:bg-blue-500/10 dark:border-blue-500/20 dark:hover:bg-blue-500/15
                          "
                          onClick={() => verClientesDaRevenda(r.id)}
                          title="Ver clientes da revenda"
                        >
                          <Users size={16} /> Clientes
                        </button>

                        {/* Ver licenças */}
                        <button
                          className="
                            px-3 py-2 rounded-xl text-sm font-semibold transition border inline-flex items-center gap-2
                            text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100
                            dark:text-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/20 dark:hover:bg-indigo-500/15
                          "
                          onClick={() => verLicencasDaRevenda(r.id)}
                          title="Ver licenças da revenda"
                        >
                          <KeyRound size={16} /> Licenças
                        </button>

                        {/* Usuários */}
                        <button
                          className="
                            px-3 py-2 rounded-xl text-sm font-semibold transition border inline-flex items-center gap-2
                            text-purple-700 bg-purple-50 border-purple-200 hover:bg-purple-100
                            dark:text-purple-200 dark:bg-purple-500/10 dark:border-purple-500/20 dark:hover:bg-purple-500/15
                          "
                          onClick={() => verUsuariosDaRevenda(r.id)}
                          title="Usuários da revenda"
                        >
                          <Users size={16} /> Usuários
                        </button>

                        {/* Editar / Excluir */}
                        <button
                          className="
                            p-2 rounded-full transition
                            text-blue-600 hover:bg-blue-100
                            dark:text-blue-300 dark:hover:bg-blue-500/10
                          "
                          onClick={() => handleEditar(r.id)}
                          title="Editar Revenda"
                        >
                          <Edit size={18} />
                        </button>

                        <button
                          className="
                            p-2 rounded-full transition
                            text-red-600 hover:bg-red-100
                            dark:text-red-300 dark:hover:bg-red-500/10
                          "
                          onClick={() => handleExcluir(r.id)}
                          title="Excluir Revenda"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {items.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-10 text-center text-gray-500 dark:text-slate-300/80">
                    Nenhuma revenda encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* paginação */}
      <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <span className="text-sm text-gray-600 dark:text-slate-300/80">{paginaInfo}</span>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setPage(1)}
            disabled={!hasPrevious}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Primeira página"
          >
            <ChevronsLeft size={18} />
          </button>

          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!hasPrevious}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Anterior"
          >
            <ChevronLeft size={18} />
          </button>

          <span className="px-2 text-sm text-gray-700 dark:text-slate-200">
            Página <b>{page}</b> de <b>{totalPages}</b>
          </span>

          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={!hasNext}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Próxima"
          >
            <ChevronRight size={18} />
          </button>

          <button
            type="button"
            onClick={() => setPage(totalPages)}
            disabled={!hasNext}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Última página"
          >
            <ChevronsRight size={18} />
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default ListaRevendas;
